import React, { useState, useEffect } from "react";
import { ShoppingCart, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { userService } from "../../_services";
import CryptoJS from "crypto-js";
import Product from "./product";
import Cart from "./cart";

/**
 * NLS Rx Marketplace
 * E-commerce platform with exact dev-v3 logic and modern Shadcn UI
 */
const Ecommerce = () => {
    const [shortname, setShortname] = useState('');
    const [fullName, setFullName] = useState('');
    const [accountInfo, setAccountInfo] = useState('');
    const [productsList, setProductsList] = useState([]);
    const [cartItems, setCartItems] = useState([]);
    const [cartOpen, setCartOpen] = useState(false);
    const [loader, setLoader] = useState(false);

    useEffect(() => {
        const usernamedata = window.atob(localStorage.getItem('hacker_user_detail'));
        setShortname(usernamedata.split(':')[0]);
        setFullName(usernamedata.split(':')[1]);
        setAccountInfo(window.atob(localStorage.getItem('_file_info')));
        localStorage.setItem('pagename', 'nls-rx-marketplace');
    }, []);

    useEffect(() => {
        const savedCart = localStorage.getItem("cartItems");
        if (savedCart) {
            try {
                const parsedCart = JSON.parse(savedCart);
                if (parsedCart && parsedCart.length > 0) {
                    setCartItems(parsedCart);
                }
            } catch (error) {
                console.error("Error loading cart:", error);
            }
        }
    }, []);

    useEffect(() => {
        // Only save if cart has been initialized (not the initial empty state)
        if (cartItems !== undefined) {
            localStorage.setItem("cartItems", JSON.stringify(cartItems));
        }
    }, [cartItems]);

    const getUserDetails = () => {
        let Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        return { userName: Usermeta_Data.split(':')[0], userId: Usermeta_Data.split(':')[1] };
    };

    const decryptJsonData = (encryptedData) => {
        try {
            const secretKey = CryptoJS.enc.Utf8.parse("PnatspGK*xvyHY77q}Wh?Uf&*we{l0x!");
            const iv = CryptoJS.enc.Utf8.parse("~6#jt_b^#Q=8iXw!");
            const encryptedHex = CryptoJS.enc.Base64.parse(encryptedData);
            const decrypted = CryptoJS.AES.decrypt(
                { ciphertext: encryptedHex },
                secretKey,
                {
                    iv: iv,
                    mode: CryptoJS.mode.CBC,
                    padding: CryptoJS.pad.Pkcs7,
                }
            );
            const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
            return JSON.parse(decryptedText);
        } catch (err) {
            return null;
        }
    };

    const GetProductsList = () => {
        setLoader(true);
        try {
            const { userName, userId } = getUserDetails();
            userService.MarketPlaceProduct(userName, userId).then((apidata) => {
                if (apidata.status === "success") {
                    const products = JSON.parse(decryptJsonData(apidata.data));
                    setProductsList(products?.ProductsList);
                }
                setLoader(false);
            });
        } catch (error) {
            setLoader(false);
        }
    };

    useEffect(() => {
        GetProductsList();
    }, []);

    const addToCart = (product, selectedPrice, selectedSize, selectedPharmacy) => {
        setCartItems((prevCart) => {
            const existingProductIndex = prevCart.findIndex(item => item.ProductID === product.ProductID);

            if (existingProductIndex !== -1) {
                const updatedCart = [...prevCart];
                updatedCart[existingProductIndex] = { ...updatedCart[existingProductIndex], selectedPrice, selectedSize, selectedPharmacy };
                return updatedCart;
            }

            return [...prevCart, { ...product, selectedPrice, selectedSize, selectedPharmacy }];
        });
    };

    const deleteCartItem = (productId) => {
        setCartItems((prevCart) => prevCart.filter(item => item.ProductID !== productId))
    };

    const updateCartPrice = (productId, newPrice) => {
        setCartItems((prevCart) =>
            prevCart.map(item =>
                item.ProductID === productId ? { ...item, selectedPrice: newPrice } : item
            )
        );
    };

    const updateProductSize = (productId, newSize) => {
        setCartItems((prevCart) =>
            prevCart.map(item =>
                item.ProductID === productId ? { ...item, selectedSize: newSize } : item
            )
        );
    };

    const updateProductPharmacy = (productId, newPharmacy) => {
        setCartItems((prevCart) =>
            prevCart.map(item =>
                item.ProductID === productId ? { ...item, selectedPharmacy: newPharmacy } : item
            )
        );
    };

    const clearCart = () => {
        setCartItems([])
    }

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">NLS Rx Marketplace</h1>
                </div>
                
                <Sheet open={cartOpen} onOpenChange={setCartOpen}>
                    <SheetTrigger asChild>
                        <Button variant="outline" size="lg" className="relative">
                            <ShoppingCart className="h-5 w-5 mr-2" />
                            Cart
                            {cartItems.length > 0 && (
                                <Badge
                                    variant="brand"
                                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 flex items-center justify-center"
                                >
                                    {cartItems.length}
                                </Badge>
                            )}
                        </Button>
                    </SheetTrigger>
                    <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
                        <SheetHeader className="sr-only">
                            <SheetTitle>Shopping Cart</SheetTitle>
                        </SheetHeader>
                        <Cart
                            cartItems={cartItems}
                            updateCartPrice={updateCartPrice}
                            updateProductSize={updateProductSize}
                            updateProductPharmacy={updateProductPharmacy}
                            setCartOpen={setCartOpen}
                            deleteCartItem={deleteCartItem}
                            clearCart={clearCart}
                        />
                    </SheetContent>
                </Sheet>
            </div>

            {/* Products Grid */}
            {loader ? (
                <Card>
                    <CardContent className="flex items-center justify-center py-12">
                        <Loader2 className="h-12 w-12 animate-spin text-accent" />
                    </CardContent>
                </Card>
            ) : productsList && productsList.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
                    {productsList.map(product => (
                        <Product 
                            key={product.ProductID} 
                            product={product} 
                            addToCart={addToCart} 
                            updateCartPrice={updateCartPrice} 
                            updateProductSize={updateProductSize} 
                            updateProductPharmacy={updateProductPharmacy} 
                            cartItems={cartItems} 
                        />
                    ))}
                </div>
            ) : (
                <div className="text-center py-12 text-muted-foreground">
                    <p className="text-lg">No products available at this time.</p>
                    <p className="text-sm mt-2">Please check back later.</p>
                </div>
            )}
        </div>
    );
};

export default Ecommerce;
